import { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { CheckCircle, XCircle, Loader2, Mail } from 'lucide-react';
import { Button } from '../components/ui/button';
import api from '../lib/api';
import useSEO from '../hooks/useSEO';

export default function VerifyEmail() {
  useSEO({ title: 'Verify email — EpisodeKit', path: '/verify-email', index: false });
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const [status, setStatus] = useState('loading'); // loading | success | error | no-token
  const [message, setMessage] = useState('');

  useEffect(() => {
    if (!token) {
      setStatus('no-token');
      return;
    }
    api.get(`/auth/verify-email?token=${token}`)
      .then(res => {
        setStatus('success');
        setMessage(res.data.message);
      })
      .catch(err => {
        setStatus('error');
        setMessage(err.response?.data?.detail || 'Verification failed. The link may be expired or invalid.');
      });
  }, [token]);

  return (
    <div className="min-h-screen bg-neutral-950 flex items-center justify-center px-4">
      <div className="max-w-md w-full bg-neutral-900 border border-neutral-800 rounded-xl p-8 text-center" data-testid="verify-email-page">
        {status === 'loading' && (
          <>
            <Loader2 className="w-12 h-12 text-violet-500 animate-spin mx-auto mb-4" />
            <h1 className="text-xl font-bold text-white mb-2">Verifying your email...</h1>
            <p className="text-neutral-400 text-sm">Please wait a moment.</p>
          </>
        )}
        {status === 'success' && (
          <>
            <CheckCircle className="w-12 h-12 text-emerald-400 mx-auto mb-4" />
            <h1 className="text-xl font-bold text-white mb-2">Email Verified</h1>
            <p className="text-neutral-400 text-sm mb-6">{message}</p>
            <Link to="/login">
              <Button className="bg-violet-600 hover:bg-violet-700 w-full" data-testid="go-to-login">Go to Login</Button>
            </Link>
          </>
        )}
        {status === 'error' && (
          <>
            <XCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
            <h1 className="text-xl font-bold text-white mb-2">Verification Failed</h1>
            <p className="text-neutral-400 text-sm mb-6">{message}</p>
            <Link to="/login">
              <Button variant="outline" className="border-neutral-700 text-neutral-300 w-full" data-testid="back-to-login">Back to Login</Button>
            </Link>
          </>
        )}
        {status === 'no-token' && (
          <>
            <Mail className="w-12 h-12 text-violet-400 mx-auto mb-4" />
            <h1 className="text-xl font-bold text-white mb-2">Check Your Email</h1>
            <p className="text-neutral-400 text-sm mb-6">
              We've sent a verification link to your email address. Click the link to activate your account.
            </p>
            <Link to="/login">
              <Button variant="outline" className="border-neutral-700 text-neutral-300 w-full">Back to Login</Button>
            </Link>
          </>
        )}
      </div>
    </div>
  );
}
